import { type ReactNode } from "react";
import type { Identifier } from "ra-core";
import { Link } from "react-router";

import { ChannelBadge } from "../notes/ChannelBadge";
import { stripInlineImages } from "../notes/inlineImages";

type ActivityLogNoteProps = {
  header: ReactNode;
  text: string;
  link: string | false;
  /** The note's channel, when it has one: shown at the start of the text. */
  channelId?: Identifier | null;
};

export function ActivityLogNote({
  header,
  text,
  link,
  channelId,
}: ActivityLogNoteProps) {
  if (!text) {
    return null;
  }

  // A pasted image is a markdown line in the text; a three-line preview has no
  // use for it. A note that is only a picture still gets a line. LCCRM-87.
  const plainText =
    stripInlineImages(text).replace(/\s+/g, " ").trim() || "(image)";

  const textElement = (
    <p className="text-sm line-clamp-3 overflow-hidden">
      <ChannelBadge channelId={channelId} className="mr-1.5 align-middle" />
      {plainText}
    </p>
  );

  return (
    <div className="p-0">
      <div className="flex flex-col space-y-2 w-full">
        <div className="flex flex-row space-x-1 items-center w-full">
          {header}
        </div>
        <div className="md:max-w-150 [&_p]:my-auto">
          {link !== false ? (
            <Link
              to={link}
              className="hover:bg-muted rounded transition-colors"
            >
              {textElement}
            </Link>
          ) : (
            textElement
          )}
        </div>
      </div>
    </div>
  );
}
